import keyMeasures from "@/data/key-measures.json"

/**
 * Source unique de la navigation du shell Labricot (barre fine + drawer).
 *
 * Le menu vivait auparavant en double : dans les `computed` d'`AppNavDrawer` (rôles MANAGER /
 * WORKER / SUPERVISOR / CONSUMER) et dans `AppHeader/navLinks` (COOK / NUTRITIONIST / RESEARCHER /
 * SURPLUSMANAGER, servis par l'ancien bandeau). Depuis « tout burger partout », tous les rôles
 * connectés passent par le drawer ; ce module rassemble les deux jeux d'entrées.
 *
 * Règle de portage : n'inclure qu'une route dont la garde (`meta.requiredPermissions`) accepte le
 * rôle. Une entrée pointant vers une route refusée renvoie l'utilisateur au dashboard (cul-de-sac).
 * Les entrées ci-dessous ont donc été filtrées route par route pour chaque rôle. Les stubs
 * (`BlankPage`, « Actions suggérées ») et les écrans refusés par la garde sont volontairement
 * absents — voir le commentaire de chaque rôle.
 *
 * Fonctions pures (aucun accès au store / à `this`) : elles reçoivent l'état en argument, ce qui
 * les rend testables isolément.
 */

// Tableau de bord par rôle. L'ordre vaut priorité : `.find` retient le premier rôle détenu, car un
// utilisateur peut cumuler plusieurs rattachements. RESEARCHER partage le dashboard nutritionniste.
const DASHBOARD_BY_ROLE = [
  ["MANAGER", "/dashboard-manager"],
  ["SUPERVISOR", "/dashboard-supervisor"],
  ["WORKER", "/dashboard-worker"],
  ["COOK", "/dashboard-cook"],
  ["NUTRITIONIST", "/dashboard-nutritionist"],
  ["SURPLUSMANAGER", "/dashboard-surplus-manager"],
  ["RESEARCHER", "/dashboard-nutritionist"],
  ["CONSUMER", "/mon-espace"],
]

// Sous-menus du gestionnaire, en accordéon (portés depuis AppHeader/navLinks, arbitrage S64).
// Chaque entrée est vérifiée route par route ET vue par vue (la route existe, la garde accepte
// MANAGER, et la vue rend un contenu). Sont écartées : `DiagnosticPage` (beforeEnter redirige tout
// connecté), `TerritoryCanteens` (réservé aux élus), `CreateEatingExperience` (NUTRITIONIST only),
// `ActionHome` (route inexistante), et `RestaurantDashboardPage` (vue-stub — doublon vide de
// l'entrée plate « Tableau de bord »).
const MANAGER_GROUPS = [
  {
    title: "Mon restaurant",
    icon: "mdi-silverware-fork-knife",
    children: [{ title: "Diagnostic du gaspillage", to: { name: "WasteDiagnosticResumePage" } }],
  },
  {
    title: "M'améliorer",
    icon: "mdi-trending-up",
    children: [
      { title: "Acteurs de l'éco-système", to: { name: "PartnersHome" } },
      { title: "Générer mon affiche", to: { name: "GeneratePosterPage" } },
      { title: "Webinaires", to: { name: "CommunityPage" } },
      { title: "Blog", to: { name: "BlogsHome" } },
    ],
  },
  {
    title: "Toutes les cantines",
    icon: "mdi-map-marker-outline",
    children: [
      { title: "Trouver une cantine", to: { name: "CanteenSearchLanding" } },
      { title: "Dans ma collectivité", to: { name: "PublicCanteenStatisticsPage" } },
    ],
  },
  {
    title: "Comprendre mes obligations",
    icon: "mdi-scale-balance",
    children: keyMeasures.map((measure) => ({
      title: measure.shortTitle,
      to: { name: "KeyMeasurePage", params: { id: measure.id } },
    })),
  },
  {
    title: "Aide",
    icon: "mdi-help-circle-outline",
    children: [
      { title: "Foire aux questions", to: { name: "FaqPage" } },
      { title: "Importer un fichier", to: { name: "DiagnosticsImporter" } },
      { title: "Contactez-nous", to: { name: "ContactPage" } },
    ],
  },
]

export const LEGAL_LINKS = [
  { title: "Mentions légales", to: { name: "LegalNotices" } },
  { title: "CGU", to: { name: "CGU" } },
  { title: "Politique de confidentialité", to: { name: "PrivacyPolicy" } },
  { title: "Accessibilité", to: { name: "AccessibilityDeclaration" } },
  { title: "Plan du site", to: { name: "SiteMap" } },
]

/**
 * Entrées plates du menu, dans l'ordre d'affichage, filtrées par rôle.
 * @param {Object}  ctx
 * @param {string[]} ctx.userRoles          rôles effectifs (store.getters.userRoles)
 * @param {Object=}  ctx.contextCanteen     cantine de contexte ({ id, name }) ou null
 * @param {boolean=} ctx.isPedagogicalLead  responsable pédagogique (habilitation serveur)
 * @returns {Array<{ title, icon, to?, href? }>}
 */
export function buildNavItems({ userRoles = [], contextCanteen = null, isPedagogicalLead = false } = {}) {
  const items = []
  const has = (role) => userRoles.includes(role)

  // Tableau de bord : cible = dashboard Labricot du rôle détenu (priorité par ordre de la table).
  const dashboard = DASHBOARD_BY_ROLE.find(([role]) => has(role))
  if (dashboard) {
    items.push({ title: "Tableau de bord", icon: "mdi-view-dashboard-outline", to: dashboard[1] })
  }

  // --- MANAGER ---
  // Modifier ma cantine : lien canonique id--nom depuis la cantine de contexte (manager seul).
  if (has("MANAGER") && contextCanteen) {
    const { id, name } = contextCanteen
    items.push({
      title: "Modifier ma cantine",
      icon: "mdi-pencil-outline",
      to: { name: "CanteenForm", params: { canteenUrlComponent: `${id}--${name || ""}` } },
    })
  }
  if (has("MANAGER")) {
    items.push({
      title: "Mes achats",
      icon: "mdi-cart-outline",
      // Filtre cantine optionnel : contextualisé quand la cantine de contexte est là, sinon la vue
      // retombe sur la cantine de l'utilisateur — l'entrée reste atteignable.
      to: contextCanteen ? { name: "PurchasesHome", query: { cantine: contextCanteen.id } } : { name: "PurchasesHome" },
    })
    items.push({ title: "Mes avis", icon: "mdi-comment-quote-outline", to: { name: "SurveyLinksHome" } })
  }

  // --- Responsable pédagogique (habilitation portée par le rattachement, pas par le rôle) ---
  if (isPedagogicalLead) {
    items.push({ title: "Atelier des cours", icon: "mdi-book-edit-outline", to: { name: "CourseStudioPage" } })
  }

  // --- COOK --- (menu porté d'AppHeader, réduit aux routes autorisées à COOK). Écartés car la
  // garde refuse COOK : « Plan d'action » (ActionPlan) et « Mes résultats » (ManageResults).
  if (has("COOK")) {
    items.push({
      title: "Diagnostic du gaspillage",
      icon: "mdi-chart-box-outline",
      to: { name: "WasteDiagnosticResumePage" },
    })
    items.push({ title: "Prochain rendez-vous", icon: "mdi-calendar-clock", to: { name: "NextMeeting" } })
    items.push({ title: "Contacter LabSoft", icon: "mdi-email-outline", to: { name: "ContactLabSoft" } })
  }

  // --- NUTRITIONIST --- « Ma patientèle » reste plate ; le suivi est en accordéon (buildNavGroups).
  if (has("NUTRITIONIST")) {
    items.push({ title: "Ma patientèle", icon: "mdi-account-group-outline", to: { name: "EatingIssuePrediction" } })
  }

  // --- CONSUMER --- remplace le faux menu de l'écran d'apprentissage (§9.5).
  if (has("CONSUMER")) {
    items.push({ title: "Mes apprentissages", icon: "mdi-school-outline", to: { name: "NutriQuest" } })
    items.push({ title: "Mon avis sur le repas", icon: "mdi-silverware-variant", to: { name: "AdultMealFeedback" } })
    items.push({ title: "Mes activités", icon: "mdi-clipboard-list-outline", to: { name: "ActionPlan" } })
  }

  // --- Commun : compte --- l'ancien bandeau offrait « Mon compte » à tous les rôles connectés ;
  // le drawer le restitue pour ne priver aucun rôle d'accès à son profil. Route sans garde.
  items.push({ title: "Mon compte", icon: "mdi-account-outline", to: { name: "AccountSummaryPage" } })

  return items
}

/**
 * Sous-menus en accordéon, filtrés par rôle.
 * @param {Object}  ctx
 * @param {string[]} ctx.userRoles  rôles effectifs (store.getters.userRoles)
 * @returns {Array<{ title, icon, children: Array<{ title, to?, href? }> }>}
 */
export function buildNavGroups({ userRoles = [] } = {}) {
  const groups = []
  const has = (role) => userRoles.includes(role)

  if (has("MANAGER")) groups.push(...MANAGER_GROUPS)

  // --- NUTRITIONIST --- (porté d'AppHeader « Mes restaurants »). « Les questionnaires » est un lien
  // hors SPA (`href`) vers le tableau de bord Django des questionnaires.
  if (has("NUTRITIONIST")) {
    groups.push({
      title: "Suivi nutritionnel",
      icon: "mdi-clipboard-pulse-outline",
      children: [
        { title: "Mes problématiques", to: { name: "ConcernsPage" } },
        { title: "Mes recommandations", to: { name: "EatingExperiencePage" } },
        { title: "Mes actions", to: { name: "ActionPlan" } },
        { title: "Les questionnaires", href: "/surveys/dashboard/" },
      ],
    })
  }

  // --- RESEARCHER --- (porté d'AppHeader « Étudier l'alimentation »). ActionPlan est absent : sa
  // garde refuse RESEARCHER (l'entrée aurait été un cul-de-sac).
  if (has("RESEARCHER")) {
    groups.push({
      title: "Étudier l'alimentation",
      icon: "mdi-flask-outline",
      children: [
        { title: "Les problématiques", to: { name: "ConcernsPage" } },
        { title: "Les recommandations", to: { name: "EatingExperiencePage" } },
        { title: "Ajouter une recommandation", to: { name: "CreateEatingExperience" } },
        { title: "Les questionnaires", href: "/surveys/dashboard/" },
      ],
    })
  }

  return groups
}
